import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { COLORS } from '@livecommerce/design-system';
import { ONE_TO_ONE_PRODUCT, ONE_TO_MANY_PRODUCT, ON_DEMAND_PRODUCT, CHAT_PRODUCT } from 'constants/products.js';
import useProductDataOutsideRoutes from 'hooks/useProductDataOutsideRoutes.js';
import useOrgId from 'hooks/useOrgId.js';
import { useFeatureContext } from 'contexts/FeatureContext.jsx';
import { trackingService } from 'services/tracking/TrackingService.js';
import { isProduction } from 'constants/gcp.js';
import useOrgType from 'hooks/useOrgType.js';
// import { FeedbackExtension } from './feedbackExtension.js'; // Disabled for now as the new agent is not compatible with it.
import { useFirebase } from 'components/Firebase/index.js';
import { ConstructCreateSalesforceCaseExtension } from './createSalesforceCaseExtension.js';

const voiceflowProjectIds = {
  [ONE_TO_ONE_PRODUCT]: '6641e7a8f009f48ec9ea429e',
  [ONE_TO_MANY_PRODUCT]: '662f9627ef66b8f006b03486',
  [ON_DEMAND_PRODUCT]: '672e13fd6a18eb2f9c0d42dc',
  [CHAT_PRODUCT]: '672e16456a18eb2f9c0d442a',
  ALL_IN_ONE: null, // When set, the same project is used for all products
};

const BOT_AVATAR_URL = 'https://cdn.liveshopping.bambuser.com/misc/ai-chatbot/bam-ai.svg';
const BAMBUSER_LOGO_URL = 'https://cdn.liveshopping.bambuser.com/misc/ai-chatbot/bam-logo.svg';
const BOT_LAUNCHER_ICON_URL = 'https://cdn.liveshopping.bambuser.com/misc/ai-chatbot/bam-ai.svg';

const SCRIPT_ID = 'voiceflow-script';
const SCRIPT_SRC = 'https://cdn.voiceflow.com/widget-next/bundle.mjs';

const createOrReuseScript = (onLoad) => {
  let voiceflowScript = document.getElementById(SCRIPT_ID);

  if (voiceflowScript?.onload) {
    voiceflowScript.onload = onLoad;
    onLoad();
    return;
  }

  voiceflowScript = document.createElement('script');
  voiceflowScript.id = SCRIPT_ID;
  voiceflowScript.src = SCRIPT_SRC;
  voiceflowScript.type = 'text/javascript';
  voiceflowScript.onload = onLoad;
  document.head.appendChild(voiceflowScript);
};

/**
 * Remove old feedback elements from the chat widget.
 * This can be used at the time of loading the chat widget.
 */
function removeOldFeedbackElements() {
  const chatWidget = document
    .querySelector('#voiceflow-chat')
    ?.shadowRoot?.querySelector('.vfrc-widget:last-child .vfrc-chat--dialog');
  // Old or completed feedback widgets do not include any div with 'vfrc-feedback' class
  const oldFeedbackWidgets = chatWidget?.querySelectorAll('.vfrc-message--extension-Feedback:not(:has(.vfrc-feedback))');
  if (oldFeedbackWidgets) {
    oldFeedbackWidgets.forEach((feedback) => {
      const messageContainer = feedback?.closest('.vfrc-system-response');
      messageContainer && messageContainer?.remove();
    });
  }
}

// Color palette for the Voiceflow chat widget theme
// See: https://docs.voiceflow.com/documentation/deploy/widget/custom-web-chat-styling#loading-your-stylesheet
const chatPalette = {
  50: COLORS.light_100, // system message backgrounds
  100: '#E8E8E8',
  200: '#D1D1D1',
  300: '#A3A3A3',
  400: '#737373',
  500: COLORS.orange_500, // primary accent / action color
  600: '#C44E0E',
  700: COLORS.dark_600, // chat background, input area
  800: COLORS.dark_900, // avatar, button backgrounds
  900: COLORS.black, // header, footer
};

// https://github.com/voiceflow/react-chat/blob/master/packages/react-chat/src/styles.css
const cssOverrides = `
  :host {
    --chat-bg: ${chatPalette[700]};
    --chat-header-bg: ${chatPalette[700]};
    --chat-message-bg: ${chatPalette[50]};
  }

  .vfrc-launcher {
    display: none;
  }

  .vfrc-assistant-info .vfrc-avatar {
    /* Do not display logo within the AI Chatbot on BamHub */
    display: none;
  }

  .vfrc-assistant-info--title {
    /* Title is already displayed in the AI Chatbot widget header */
    display: none;
  }

  .vfrc-chat {
    /* Main widget background color */
    background-color: var(--chat-bg);
  }

  .vfrc-chat__container {
    /* Center the widget */
    margin: auto 0;
  }

  .vfrc-header {
    background-color: var(--chat-header-bg);
  }

  .vfrc-chat .vfrc-footer__content {
    /* Override footer content background */  
    background: var(--chat-bg);
    
    /* Nicer border between messages and input field  */
    padding-top: 10px;
    box-shadow: 0px 0px 50px 0px var(--chat-bg);
  }

  .vfrc-system-response .vfrc-message {
    background-color: var(--chat-message-bg);
  }
  `;

const useVoiceFlow = () => {
  const [ initialized, setInitialized ] = useState(!!window.voiceflow?.chat);

  // Load the VoiceFlow chat library and wait for it to be initialized
  useEffect(() => {
    if (!initialized) {
      createOrReuseScript(() => {
        const checkLibraryLoaded = () => {
          if (window.voiceflow?.chat) {
            setInitialized(true);
            return;
          }
          setTimeout(checkLibraryLoaded, 10);
        };
        checkLibraryLoaded();
      });
    }
  }, [initialized]);

  return window.voiceflow?.chat;
};

const getVoiceFlowConfig = ({ orgId, userId, userEmail, projectId, selectedProduct, orgType, extensions }) => {
  return {
    verify: { projectID: projectId },
    userID: `uid-${userId}-org-${orgId}`,
    versionID: isProduction ? 'production' : 'development',
    user: {
      name: userEmail || `OrgID: ${orgId}, userID: ${userId}`,
    },
    assistant: {
      renderMode: 'popover',
      image: BOT_AVATAR_URL,
      header: {
        hideImage: false,
        imageUrl: BOT_LAUNCHER_ICON_URL,
      },
      watermark: false,
      color: chatPalette[800],
      palette: chatPalette,
      position: 'right',
      extensions: [...extensions],
      stylesheet: `data:text/css;base64,${window.btoa(cssOverrides)}`,
      persistence: 'localStorage',
    },
    launch: {
      event: {
        type: 'launch',
        payload: {
          orgId,
          userId,
          internalUserEmail: userEmail, // Only internal user email
          selectedProduct,
          pageUrl: window?.location.href,
          organizationType: orgType || 'default',
        },
      },
    },
  };
};

const getVoiceflowProjectId = (selectedProduct) =>
  voiceflowProjectIds.ALL_IN_ONE || voiceflowProjectIds[selectedProduct] || null;

const useChatBotAgent = () => {
  const { features } = useFeatureContext();
  const { aiChatBotEnabled } = features;
  const orgId = useOrgId();
  const { selectedProduct } = useProductDataOutsideRoutes();
  const uid = useSelector((state) => state.sessionState.authUser?.uid);
  const userEmail = useSelector((state) => state.sessionState.authUser?.email);
  const internalUserEmail = userEmail && userEmail.endsWith('@bambuser.com') ? userEmail : null;
  const [ isOpen, setIsOpen ] = useState(false);
  const [ isLoaded, setIsLoaded ] = useState(false);
  // State to track the selected product currently known by the chatbot
  const [ cachedSelectedProduct, setCachedSelectedProduct ] = useState(selectedProduct);
  const client = useVoiceFlow();
  const orgType = useOrgType();
  const firebase = useFirebase();

  useEffect(() => {
    if (!aiChatBotEnabled) return;
    if (!uid || !orgId || !client) return;
    if (isLoaded && selectedProduct && cachedSelectedProduct === selectedProduct) return;
    if (!firebase) return;
    if (!userEmail) return; // We need user email to create Salesforce cases
    const projectId = getVoiceflowProjectId(selectedProduct);
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      const salesforceCreateCaseExtension = await ConstructCreateSalesforceCaseExtension(firebase, { orgId, userEmail });
      const config = await getVoiceFlowConfig({
        orgId,
        userId: uid,
        userEmail: internalUserEmail,
        projectId,
        selectedProduct,
        orgType,
        extensions: [
          ...(salesforceCreateCaseExtension ? [salesforceCreateCaseExtension] : []),
          // FeedbackExtension,  // Disabled for now as the new agent is not compatible with it.
        ],
      });
      const loadPromise = client.load(config);
      loadPromise.then(() => {
        setIsLoaded(true);
      });

    })();
    return () => { cancelled = true; };
  }, [
    aiChatBotEnabled,
    selectedProduct,
    cachedSelectedProduct,
    uid,
    orgId,
    client,
    orgType,
    isLoaded,
    internalUserEmail,
    userEmail,
    firebase,
  ]);

  // Listen for messages from the AI ChatBot
  useEffect(() => {
    if (!client) return;
    const onMessage = (message) => {
      if (message?.data === '{"type":"voiceflow:close"}') {
        setIsOpen(false);
      }
      if (message?.data === '{"type":"voiceflow:open"}') {
        removeOldFeedbackElements(); // Feedback widget (extension) leaves some empty message boxes
        setIsOpen(true);
      }
    };
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [client]);

  const open = useCallback(() => {
    // Initialize or update cachedSelectedProduct state ONLY when opening the chatbot!
    if (!cachedSelectedProduct) setCachedSelectedProduct(selectedProduct);
    if (cachedSelectedProduct !== selectedProduct) setCachedSelectedProduct(selectedProduct);

    client.open();
    trackingService.track({ eventType: 'chat-bot:open' });
  }, [client, selectedProduct, cachedSelectedProduct]);

  const close = useCallback(() => {
    client.close();
    trackingService.track({ eventType: 'chat-bot:close' });
  }, [client]);

  // Close the chat bot when we change products
  useEffect(() => {
    if (client) {
      client.close();
    }
  }, [selectedProduct, client]);

  // Send switchedProduct event to Voiceflow runtime ONLY when cachedSelectedProduct changes
  // (to avoid sending the same event multiple times which consumes credits)
  // Note: This is only relevant when using the same Voiceflow project for all products (ALL_IN_ONE)
  useEffect(() => {
    if (!voiceflowProjectIds.ALL_IN_ONE) return; // Only relevant when using same Voiceflow project for all products
    if (!isOpen) return;
    if (!cachedSelectedProduct) return;
    window?.voiceflow?.chat?.interact({
      type: 'event',
      payload: {
        event: {
          name: 'switchedProduct', // The event name defined in your Event CMS
          data: {
            selectedProduct: cachedSelectedProduct,
          },
        },
      },
    });
  }, [cachedSelectedProduct, isOpen]);

  return {
    isOpen,
    isInitialized: !!client,
    open,
    close,
  };
};

export default useChatBotAgent;
